import { BaseGamePadWebDrawer } from 'bog-arcade-core-ui/drawers';

import { gamePadWebResource } from '../../../core/resources';
import { GameLayers, GameSceneType } from '../../../core/enums';
import { webConfig } from '../../../core/applicationConfig';
import { resourceHelper } from '../../../core/resourceHelper';
import { sharedSourceNames } from '../../../core/sourceNames';
import { gamePadService } from '../../../services/helperServices/GamePadService';
import {
  autoSpinSubscriber,
  betAmountSubscriber,
  decrementAutoSpinCount,
  decrementNextQuickBet,
  handleAutoSpinClick,
  handleQuickBetsIndexChange,
  handleSpinButtonCLick,
  handleSpinCountsActiveChange,
  handleSwitchBall,
  incrementAutoSpinCount,
  incrementNextQuickBet,
  spinButtonSubscriber,
  switchButtonSubscriber,
} from '../events';
import {
  autoSpinCountTextStyle,
  betAmountValueTextStyle,
} from '../../../core/textStyles';
import { balanceLastWinDrawer } from '../../balanceLastWin/BalanceLastWinDrawer';
import {
  CANVAS_GAME_LOADED,
  GAME_APPLICATION_SCENE_STATE,
  HISTORY_ICON_STATE, INFO_ICON_STATE,
} from '../../../stateManagment';
import { popupService } from '../../../services/helperServices/PopupService';
import { autoSpinHelper } from '../../../services/helperServices/AutoSpinService';
import { animationDrawer } from '../../../core/AnimationDrawer';
import { bigWinService } from '../../../services/helperServices/BigWinService';
import { handleBigWinClick } from '../../bigWinPopup/helpers';
import { bigWinPopup } from '../../bigWinPopup/BigWinPopupDrawer';
import { debounce, delay } from 'bog-arcade-core-ui/helpers';

export class GamePadWebDrawer extends BaseGamePadWebDrawer {
  constructor() {
    super();
    this.mainContainer = this.createContainer('mainContainer');
    document.addEventListener('keydown', debounce((e) => {
      if (e.code === 'Space' && CANVAS_GAME_LOADED.getValue()) {
        if (popupService.congratulationsPopupState) {
          return popupService.closeCongratulationsPopup();
        } else if (popupService.buyBonusPopupState) {
          return popupService.closeBuyBonusPopup();
        } else if (bigWinService.state) {
          return handleBigWinClick(e, bigWinPopup.application);
        } else if (HISTORY_ICON_STATE.getValue() || INFO_ICON_STATE.getValue()) {
          HISTORY_ICON_STATE.next(false);
          INFO_ICON_STATE.next(false);
          return null;
        } else if (gamePadService.spinState && !gamePadService.currentSpinCount) {
          {
            animationDrawer.spin(this.spinButton);
            return handleSpinButtonCLick(e);
          }
        }
      }
    }, 300, true));
  }

  async loadResource() {
    await super.loadResource(gamePadWebResource, this.createLoader()).then(({ res }) => {
      this.resources = res;
    });
  }

  setupLayers(layers) {
    this.container = layers[GameLayers.gamePad];
    this.footerContainer = layers[GameLayers.footer];
  }

  setContainerPosition() {
    this.setPosition(this.container, { y: webConfig.height - 130 });
  }

  setFooter() {
    const [footer] = this.getFooter();
    this.setPosition(footer, { y: 1050 });
    this.footerContainer.addChild(footer);
  }

  setRect() {
    const rect = this.getGamePadRect();
    this.container.addChild(rect);
  }

  setSpinButton() {
    const [content, button] = this.getSpinButton({ scale: 1.3 });
    const [autoSpinContent, countTexture, infinityTexture] = this.getAutoSpinButton({
      scale: 1.3,
      textStyle: autoSpinCountTextStyle,
    });
    const animateSpin = () => animationDrawer.spin(button);
    this.setPosition(content, { x: 1270, y: -20 });
    this.setPosition(autoSpinContent, { x: 1270, y: -20 });
    this.spinButton = button;

    content.visible = true;
    autoSpinContent.visible = false;
    infinityTexture.visible = false;

    this.addSubscriber(gamePadService.autoSpinButtonSubscribe((state) => {
      if (!gamePadService.currentSpinCount) {
        autoSpinContent.visible = state;
        content.visible = !state;
      } else {
        autoSpinContent.visible = true;
        content.visible = false;
      }
    }));

    this.registerClick(content, (event) => {
      if (gamePadService.spinState) {
        animateSpin();
      }
      handleSpinButtonCLick(event);
    });
    this.registerClick(autoSpinContent, () => autoSpinHelper.end());

    this.addSubscriber(gamePadService.subscribeCurrentCountChange((state) => {
      if (state === Infinity) {
        countTexture.visible = false;
        infinityTexture.visible = true;
      } else {
        infinityTexture.visible = false;
        countTexture.visible = true;
        countTexture.text = state;
      }
    }));

    this.addSubscriber(gamePadService.spinButtonSubscribe(state => {
      spinButtonSubscriber(state, content);
    }));

    this.mainContainer.addChild(content, autoSpinContent);
  }

  setSwitchButton() {
    const switchTexture = this.createSprite(resourceHelper.get(sharedSourceNames.switchIcon));
    switchTexture.anchor.set(.5);
    switchTexture.scale.set(.5);
    this.setupButtonMode(switchTexture);
    this.registerClick(switchTexture, (event) => {
      handleSwitchBall(event);
    });

    this.addSubscriber(gamePadService.switchButtonSubscribe(state => {
      switchButtonSubscriber(state, switchTexture);
    }));
    this.setPosition(switchTexture, { x: 1400, y: -150 });
    this.mainContainer.addChild(switchTexture);
  }

  setBetAmount() {
    const [container, renderOption, { prev, next }] = this.getAmountContent({
      onNext: incrementNextQuickBet,
      onPrev: decrementNextQuickBet,
      amountTextStyle: betAmountValueTextStyle,
    });
    this.setPosition(container, { x: webConfig.width / 2, y: 50 });
    this.addSubscriber(gamePadService.subscribeQuickBet(renderOption));
    this.addSubscriber(gamePadService.subscribeQuickBetsIndex(index => {
      handleQuickBetsIndexChange(index, { prev, next });
    }));
    this.addSubscriber(gamePadService.betAmountSubscribe(state => {
      betAmountSubscriber(state, container);
    }));

    this.mainContainer.addChild(container);
  }

  setSpinAmount() {
    const [container, renderOption, onToggle, { prev, next, content }] = this.getAutoSpinContent({
      onNext: incrementAutoSpinCount,
      onPrev: decrementAutoSpinCount,
      valueStyle: betAmountValueTextStyle,
      textStyle: betAmountValueTextStyle,
    });
    this.setPosition(container, { x: 1550, y: 50 });
    this.addSubscriber(gamePadService.subscribeSpinCountsActiveState(onToggle));
    this.addSubscriber(gamePadService.subscribeAutoSpinCount(renderOption));
    this.addSubscriber(gamePadService.subscribeSpinCountIndex(index => {
      handleSpinCountsActiveChange(index, { prev, next });
    }));
    this.addSubscriber(gamePadService.autoSpinSubscribe(state => {
      autoSpinSubscriber(state, container);
    }, true));
    this.addSubscriber(popupService.subscribeBuyBonusPopup((isOpen, { prev }) => {
      if (isOpen && gamePadService.autoSpinActiveState) {
        autoSpinHelper.pause();
      } else if (!isOpen && gamePadService.currentSpinCount > 0) {
        delay(() => {
          autoSpinHelper.resume();
        }, 1000);
      }
    }));

    this.setupButtonMode(content);
    this.registerClick(content, handleAutoSpinClick);

    this.mainContainer.addChild(container);
  }

  setLastWinAndBalance(isBonus = false) {
    const container = this.createContainer('LastWinAndBalance');
    const content = balanceLastWinDrawer.getContent({ isBonus, sizes: { w: 200 } });
    this.setPosition(container, { x: 300, y: 15 });
    container.addChild(content);
    this.container.addChild(container);
  }

  executeBonus() {
    this.mainContainer.removeChildren();
  }

  executeMain() {
    this.mainContainer.removeChildren();
    this.setSpinButton();
    this.setBetAmount();
    this.setSpinAmount();
    this.setSwitchButton();
    this.container.addChild(this.mainContainer);
  }

  execute() {
    this.setContainerPosition();
    this.setRect();
    this.setFooter();
    this.addSubscriber(GAME_APPLICATION_SCENE_STATE.subscribe((state) => {
      if (state === GameSceneType.main) {
        this.executeMain();
      } else {
        this.executeBonus();
      }
    }, true));
    this.setLastWinAndBalance();
  }
}
